import React, { useRef } from 'react';
import { useDispatch } from 'react-redux';
import config from 'config/config';
import i18n from 'src/utils/translate';
import { showLoginModal, showSignupModal } from 'src/modules/shared/modal/actions';

import styled from 'styled-components';
import { FadeBottom } from 'styles-js/mixins/fade';
import { titleLarge, titleMedium } from 'styles-js/mixins/typography';
import { LinkButton } from 'styles-js/buttons';

const Container = styled.div`
  position: relative;
  margin-bottom: 60px;
  background-color: ${props => props.theme.colorWhite};
  ${FadeBottom};

  ::before {
    background: linear-gradient(to top, ${props => props.theme.colorWhite}, hsla(0,0%,100%,0));
  }

  @media (max-width: ${props => props.theme.screenXsMin}) {
    ::before {
      top: -50px;
      height: 50px;
    }
  }
`;

const Title = styled.p`
  ${titleLarge};
  font-family: ${props => props.theme.fontFamilyMain};
  font-weight: ${props => props.theme.fontWeightBold};
  padding-top: 10px;
  margin-bottom: 20px;
`;

const Form = styled.form`
  margin: 5px 0 25px;
`;

const EmailInput = styled.input`
  @media (max-width: ${props => props.theme.screenXsMax}) {
    height: 42px;
    line-height: 42px;
  }
`;

const JoinButton = styled.button`
  padding: 13px 20px;

  @media (max-width: ${props => props.theme.screenXsMax}) {
    padding: 9px 20px;
  }
`;

const Subtitle = styled.p`
  ${titleMedium};
  font-family: ${props => props.theme.fontFamilyMain};
  font-weight: ${props => props.theme.fontWeightBold};
  margin: 28px 0 25px;
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 25px;
`;

const ListItem = styled.li`
  overflow: hidden;
  display: flex;
  align-items: center;
  margin-bottom: 10px;
`;

const ItemImage = styled.img`
  float: left;
  margin-right: 15px;
  width: 80px;
  height: 80px;
`;

const ItemTextContainer = styled.div``;

const ItemTitle = styled.p`
  ${titleMedium};
  margin-bottom: 4px;
`;

const RatingContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 25px;

  @media (max-width: ${props => props.theme.screenXsMin}) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Stars = styled.img`
  width: 265px;
  height: 45px;

  @media (max-width: ${props => props.theme.screenXsMin}) {
    margin-bottom: 20px;
  }
`;

const RatingText = styled.p`
  color: ${props => props.theme.colorGreyDark};
  margin-left: 15px;

  @media (max-width: ${props => props.theme.screenXsMin}) {
    margin-left: 0;
  }
`;

const QuotesContainer = styled.div`
  margin-bottom: 35px;
`;

const Quote = styled.p`
  ${titleMedium};
  font-style: italic;
  color: ${props => props.theme.colorGreyDark};
  margin-bottom: 30px;
`;

const MoreLinks = styled.p`
  border-top: 1px solid ${props => props.theme.colorGreyLight};
  padding-top: 10px;
`;

const items = [
  {
    key: 1,
    title: 'Connect',
    description: 'Learn from others who\'ve been there before',
    image: `${config.cdn.static}img/visitor-block-message/connect.png`,
  },
  {
    key: 2,
    title: 'Learn',
    description: 'Ask questions, get answers 24/7',
    image: `${config.cdn.static}img/visitor-block-message/learn.png`,
  },
  {
    key: 3,
    title: 'Empower',
    description: 'Share your health experience to help others',
    image: `${config.cdn.static}img/visitor-block-message/empower.png`,
  },
];

const SecondPageBlockMessageBox = () => {
  const dispatch = useDispatch();
  const emailRef = useRef<HTMLInputElement>(null);

  const onSubmitEmail = (e: any): void => {
    const emailValue = emailRef.current ? emailRef.current.value : '';
    e.preventDefault();
    dispatch(showSignupModal(null, null, false, emailValue));
  };

  return (
    <Container>
      <Title>{i18n.t('To continue reading this post, create a free account')}</Title>
      <p>{i18n.t('It only takes a minute!')}</p>
      <Form className="home__form" onSubmit={e => onSubmitEmail(e)}>
        <EmailInput
          aria-label="Please enter your email address"
          className="form-control home__form-input home__form-input--small"
          placeholder={i18n.t('email@address.com')}
          ref={emailRef}
          type="text"
        />
        <JoinButton className="btn btn-primary" type="submit">{i18n.t('Join')}</JoinButton>
      </Form>
      <p>{i18n.t('Already a member?')} <LinkButton onClick={() => dispatch(showLoginModal())}>{i18n.t('Log in')}</LinkButton></p>

      <Subtitle>{i18n.t('The world\’s largest social network for health')}</Subtitle>
      <List>
        {items.map(item => (
          <ListItem key={item.key}>
            <ItemImage alt={item.title} src={item.image} />
            <ItemTextContainer>
              <ItemTitle>{item.title}</ItemTitle>
              <p>{item.description}</p>
            </ItemTextContainer>
          </ListItem>
        ))}
      </List>

      <RatingContainer>
        <Stars alt={i18n.t('5 stars rating')} src={`${config.cdn.static}img/visitor-block-message/stars.svg`} />
        <RatingText>{i18n.t('4.8 out of 5 based on 2.3K reviews')}</RatingText>
      </RatingContainer>

      <QuotesContainer>
        <Quote>{i18n.t('“So much info from real people on so many things.”')}</Quote>
        <Quote>{i18n.t('“Sometimes I feel like no one understands and people here are just like me. I don\'t know how it got started but it\'s the best thing that has happened to me.”')}</Quote>
      </QuotesContainer>

      <MoreLinks>
        {i18n.t('Please')}
        {' '}
        <LinkButton onClick={() => dispatch(showSignupModal())}>{i18n.t('join')}</LinkButton>
        {' ' + i18n.t('or') + ' '}
        <LinkButton onClick={() => dispatch(showLoginModal())}>{i18n.t('log in')}</LinkButton>
        {' '}
        {i18n.t('to continue reading')}
      </MoreLinks>
    </Container>
  );
};

export default SecondPageBlockMessageBox;
